import { motion } from "framer-motion";
import { PhoneCall, Truck, BadgeDollarSign, ShieldCheck } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const STEPS = [
  {
    icon: PhoneCall,
    title: "Call or Book Online",
    text: "Reach us 7 days a week by phone or the 60-second online form. Tell us the appliance and the symptoms — that's all we need.",
  },
  {
    icon: Truck,
    title: "Same-Day Dispatch",
    text: "A fully stocked service van is routed to your door — often within hours — anywhere in Toronto or the GTA.",
  },
  {
    icon: BadgeDollarSign,
    title: "In-Person Diagnosis — $80, Waived on Repair",
    text: "The technician diagnoses the fault in person and gives you a flat per-job quote — never hourly. Repairs start at only $80, and approve the repair on the spot and the service call is free.",
  },
  {
    icon: ShieldCheck,
    title: "Fixed & Warranted in Writing",
    text: "Most repairs finish in one visit. Every job is backed in writing — 1 full year on parts and 30 days on labour.",
  },
];

const STATS = [
  { value: "1,480+", label: "GTA Repairs Completed" },
  { value: "4.9/5", label: "Average Customer Rating" },
  { value: "From $80", label: "Flat Per-Job Repairs" },
  { value: "1 Yr + 30 Day", label: "Parts & Labour Warranty" },
];

export default function WhyUs() {
  return (
    <section id="why-us" className="bg-white py-20 sm:py-28" data-testid="why-us-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          number="02"
          caption="How It Works"
          title="From Breakdown to Fixed — in Four Moves."
          sub="No runaround, no hourly billing games. A simple, honest process built around getting your home back to normal today."
        />

        <div className="grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-2">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                data-testid={`process-step-${i + 1}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group relative border-l-2 border-slate-200 pl-8 transition-colors duration-300 hover:border-rescue"
              >
                <span className="pointer-events-none absolute -left-1 top-0 font-mono text-6xl font-bold text-slate-100 transition-colors duration-300 group-hover:text-red-100 sm:text-7xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="relative">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-navy">{step.title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
                    {step.text}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="grain relative mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-navy-dark sm:mt-20 lg:grid-cols-4"
          data-testid="stats-band"
        >
          {STATS.map((stat) => (
            <div key={stat.label} className="bg-navy px-6 py-8 text-center">
              <p className="font-display text-3xl font-black text-white sm:text-4xl">{stat.value}</p>
              <p className="mt-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 sm:text-xs">
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
